import './index.css';
import { useMemo } from 'react';
import { M as MASKITO_DEFAULT_OPTIONS, a as maskitoDateTimeOptionsGenerator, m as maskitoDateOptionsGenerator, b as maskitoTimeOptionsGenerator, u as useMaskito } from './index.esm-l1G7dKwM.js';

/**
 * Округление времени (в формате 00:00) до ближайшего заданного шага
 *
 * @param dateTime {String} Строка с постфиксом HH:MM, где H - часы, M - минуты
 * @param roundValue {Number} Шаг для округления
 * @returns {String}
 *
 * @example
 * roundTime('20.12.2023 15:19', 15); // '20.12.2023 15:15'
 * @example
 * roundTime('15:31', 60); // '16:00'
 */
const roundTime = (dateTime, roundValue) => {
    const regexp = /(\d{2}):(\d{2})$/;
    const match = dateTime.match(regexp);
    const time = match?.[0];
    if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;
        const roundedMinutes = Math.round(totalMinutes / roundValue) * roundValue;
        const roundedHours = Math.floor(roundedMinutes / 60) % 24;
        const formattedHours = String(roundedHours).padStart(2, '0');
        const formattedMinutes = String(roundedMinutes % 60).padStart(2, '0');
        return dateTime.replace(regexp, `${formattedHours}:${formattedMinutes}`);
    }
    return dateTime;
};
/** Постпроцессор для округления времени до заданного шага */
const roundTimePostprocessor = (enabled, rounding) => (state) => enabled && rounding ? { ...state, value: roundTime(state.value, rounding) } : state;
/** Препроцессор для автоматического добавления разделителей из маски */
const autoSeparatorsPreprocessor = (mask) => {
    let lastActionIsInsert = false;
    return (state, actionType) => {
        if (actionType === 'insert') {
            lastActionIsInsert = true;
            return state;
        }
        const canAddSeparator = actionType === 'validation' && lastActionIsInsert;
        lastActionIsInsert = false;
        if (canAddSeparator) {
            const { value, selection } = state.elementState;
            const [from, to] = selection;
            let newValue = '';
            const dateArray = value.split('');
            for (let i = 0; i < mask.length; i++) {
                if (i < dateArray.length) {
                    newValue += dateArray[i];
                }
                else if (typeof mask[i] === 'string') {
                    newValue += mask[i];
                }
                else {
                    break;
                }
            }
            const addedCharacters = newValue.length - value.length;
            return {
                ...state,
                elementState: {
                    value: newValue,
                    selection: [from + addedCharacters, to + addedCharacters],
                },
            };
        }
        return state;
    };
};
/** Препроцессор для замены разделителей "., :;/_" на точку */
const replaceSeparatorsPreprocessor = (state, actionType) => {
    if (actionType === 'insert') {
        return {
            ...state,
            data: state.data
                .replace(/[^0-9., :;/_]/g, '')
                .replace(/[., :;/_]/g, '.')
                .replace(/([^0-9])\1+/g, '$1'),
        };
    }
    return state;
};
/** Постпроцессор для преобразования 4 -> 04 при вводе с маской dd */
const formatDDMaskPostprocessor = (state) => {
    if (state.value.length === 1 && Number(state.value) > 3) {
        const [from, to] = state.selection;
        return {
            ...state,
            value: state.value.padStart(2, '0'),
            selection: [from + 1, to + 1],
        };
    }
    return state;
};
const useDateTimeMask = (dateMask, timeMask, timeRounding) => {
    const options = useMemo(() => {
        // маска 'dd' - исключение, которого нету в maskito api:
        if (dateMask === 'dd') {
            return {
                mask: /^([0-9]|0\d|1\d|2[0-9]|3[01])$/,
                postprocessors: [formatDDMaskPostprocessor],
            };
        }
        const dateSeparator = '.';
        const dateMode = dateMask?.replace(/\./g, '/');
        const timeMode = 'HH:MM';
        let options = { ...MASKITO_DEFAULT_OPTIONS };
        if (dateMask && timeMask) {
            options = maskitoDateTimeOptionsGenerator({
                dateMode,
                timeMode,
                dateSeparator,
            });
        }
        else if (dateMask) {
            options = maskitoDateOptionsGenerator({
                mode: dateMode,
                separator: dateSeparator,
            });
        }
        else if (timeMask) {
            options = maskitoTimeOptionsGenerator({
                mode: timeMode,
            });
        }
        return {
            ...options,
            preprocessors: [
                replaceSeparatorsPreprocessor,
                ...(options.preprocessors || []),
                autoSeparatorsPreprocessor(options.mask),
            ],
            postprocessors: [
                ...(options.postprocessors || []),
                roundTimePostprocessor(Boolean(timeRounding && timeMode), timeRounding),
            ],
        };
    }, [dateMask, timeMask, timeRounding]);
    return useMaskito({ options });
};

export { useDateTimeMask };
//# sourceMappingURL=useDateTimeMask.js.map
